import React, { useState, useEffect, useRef } from "react";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Form, Select, DatePicker, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../features/cutomers/customerSlice";
import { getStates } from "../features/statecity/statecitySlice";
import { getSchoolVisitTarget } from "../features/schools/schoolSlice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <h2>Something went wrong.</h2>
          <p>{this.state.error.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const SchoolVisit = () => {
  const [chartInstance, setChartInstance] = useState(null);
  const [activeView, setActiveView] = useState("stateWise");
  const [selectedState, setSelectedState] = useState("");
  const [vcDatas, setVcDatas] = useState([]);
  const [selectedVC, setSelectedVC] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [filteredData, setFilteredData] = useState(null);

  const chartInstanceRef = useRef(null);
  const pdfExportRef = useRef(null);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getStates());
    dispatch(getUsers());
    dispatch(getSchoolVisitTarget());
  }, [dispatch]);

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );
  const vclistState = useSelector((state) => state.customer.customers || []);
  const schoolvisitState = useSelector(
    (state) => state.school.schoolvisits || {}
  );

  useEffect(() => {
    console.log("schoolvisitState:", schoolvisitState);
    console.log("statelistState:", statelistState);
    if (
      schoolvisitState?.stateVisitCounts &&
      Object.keys(schoolvisitState.stateVisitCounts).length
    ) {
      const { stateData, vcData } = processData();
      setFilteredData({ stateData, vcData });
    } else {
      console.warn("schoolvisitState.stateVisitCounts is empty or undefined");
      setFilteredData({ stateData: {}, vcData: {} });
    }
  }, [schoolvisitState, statelistState]);

  // Process data based on the new JSON structure
  const processData = () => {
    const stateData = {};
    const vcData = {};

    // Process stateVisitCounts
    if (schoolvisitState.stateVisitCounts) {
      Object.entries(schoolvisitState.stateVisitCounts).forEach(
        ([state, data]) => {
          stateData[state] = {
            total: data.total || 0,
            completed: data.visited || 0,
            pending: data.pending || 0,
          };
        }
      );
    }

    // Process vcVisitCounts
    if (schoolvisitState.vcVisitCounts) {
      Object.entries(schoolvisitState.vcVisitCounts).forEach(([vc, data]) => {
        vcData[vc] = {
          total: data.total || 0,
          completed: data.visited || 0,
          pending: data.pending || 0,
        };
      });
    }

    console.log("Processed State Data:", stateData);
    console.log("Processed VC Data:", vcData);
    return { stateData, vcData };
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    const filteredVCData = vclistState.filter(
      (customer) => customer.state === value && customer.role === "vc"
    );
    console.log("Filtered VC Data for State:", filteredVCData);
    setVcDatas(filteredVCData);
    setSelectedVC(""); // Reset VC selection
  };

  const handleFilterSubmit = (values) => {
    console.log("Filter Values:", values);

    let filteredStateData = {};
    let filteredVcData = {};

    if (schoolvisitState.stateVisitCounts) {
      Object.entries(schoolvisitState.stateVisitCounts).forEach(
        ([state, data]) => {
          const stateMatch =
            values.state_name &&
            state.includes(values.state_name.split(" (")[0]);
          if (values.state_name && !stateMatch) return;

          filteredStateData[state] = {
            total: data.total,
            completed: data.visited,
            pending: data.pending,
          };
        }
      );
    }

    if (schoolvisitState.vcVisitCounts) {
      Object.entries(schoolvisitState.vcVisitCounts).forEach(([vc, data]) => {
        if (values.vc_name && vc !== values.vc_name) return;

        filteredVcData[vc] = {
          total: data.total,
          completed: data.visited,
          pending: data.pending,
        };
      });
    }

    console.log("Filtered State Data:", filteredStateData);
    console.log("Filtered VC Data:", filteredVcData);

    setFilteredData({ stateData: filteredStateData, vcData: filteredVcData });
  };

  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedVC("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setVcDatas([]);
    const { stateData, vcData } = processData();
    setFilteredData({ stateData, vcData });
  };

  const stateWiseData = filteredData
    ? {
        labels: Object.keys(filteredData.stateData),
        datasets: [
          {
            label: "Total Visits",
            data: Object.values(filteredData.stateData).map((d) => d.total),
            backgroundColor: "#f87171",
          },
          {
            label: "Completed Visits",
            data: Object.values(filteredData.stateData).map((d) => d.completed),
            backgroundColor: "#4ade80",
          },
        ],
      }
    : { labels: [], datasets: [] };

  const vcWiseData = filteredData
    ? {
        labels: Object.keys(filteredData.vcData),
        datasets: [
          {
            label: "Total Visits",
            data: Object.values(filteredData.vcData).map((d) => d.total),
            backgroundColor: "#f87171",
          },
          {
            label: "Completed Visits",
            data: Object.values(filteredData.vcData).map((d) => d.completed),
            backgroundColor: "#4ade80",
          },
        ],
      }
    : { labels: [], datasets: [] };

  const renderChart = (data) => {
    if (chartInstance) {
      chartInstance.destroy();
    }
    const ctx = chartInstanceRef.current.getContext("2d");
    const newChartInstance = new Chart(ctx, {
      type: "bar",
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
        },
      },
    });
    setChartInstance(newChartInstance);
  };

  useEffect(() => {
    if (filteredData && chartInstanceRef.current) {
      renderChart(activeView === "stateWise" ? stateWiseData : vcWiseData);
    }
  }, [filteredData, activeView]);

  const handleViewChange = (view) => {
    setActiveView(view);
    if (filteredData) {
      renderChart(view === "stateWise" ? stateWiseData : vcWiseData);
    }
  };

  const exportAsPDF = () => {
    const input = pdfExportRef.current;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("school_visit_report.pdf");
    });
  };

  return (
    <ErrorBoundary>
      <div
        style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
        ref={pdfExportRef}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h4
            style={{
              fontSize: "large",
              fontWeight: "bold",
              margin: "-20px 0 20px -20px",
            }}
          >
            School Visit Targets: Planned vs Actual
          </h4>
          <p
            style={{
              textAlign: "right",
              fontSize: "0.875rem",
              margin: "-20px 20px 0 0",
              color: "rgb(107, 114, 128)",
            }}
          >
            Last Updated: 10:30 AM
          </p>
        </div>

        <Form
          form={form}
          layout="inline"
          onFinish={handleFilterSubmit}
          style={{ marginTop: "20px", marginLeft: "80px" }}
        >
          <Form.Item
            name="state_name"
            rules={[{ required: true, message: "Select State!" }]}
          >
            <Select
              placeholder="Select State"
              onChange={handleStateChange}
              style={{ width: 150 }}
            >
              {statelistState.map((i, j) => (
                <Select.Option key={j} value={i.state}>
                  {i.state_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="vc_name">
            <Select
              placeholder="Select VC"
              onChange={(value) => setSelectedVC(value)}
              style={{ width: 150, marginLeft: 16 }}
              disabled={!selectedState}
            >
              <Select.Option value="">All VCs</Select.Option>
              {vcDatas.map((item) => (
                <Select.Option key={item._id} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="start_date">
            <DatePicker
              placeholder="From"
              style={{ width: 100, marginLeft: 25, marginTop: 16 }}
              onChange={(value) => {
                setSelectedStartDate(value ? value.format("YYYY-MM-DD") : null);
              }}
            />
          </Form.Item>

          <Form.Item name="end_date">
            <DatePicker
              placeholder="To"
              style={{ width: 100, marginBottom: 16, marginTop: 16 }}
              onChange={(value) => {
                setSelectedEndDate(value ? value.format("YYYY-MM-DD") : null);
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button
              style={{
                backgroundColor: "#28a745",
                color: "#fff",
                marginTop: 16,
                marginLeft: 20,
              }}
              htmlType="submit"
            >
              Apply Filter
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              style={{
                backgroundColor: "#dc3545",
                color: "#fff",
                marginTop: 16,
                marginLeft: -12,
              }}
              onClick={resetFilters}
            >
              Reset Filter
            </Button>
          </Form.Item>
        </Form>

        <div
          style={{
            backgroundColor: "white",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "0.5rem",
            padding: "1rem",
            marginTop: "0.5rem",
            height: "300px",
          }}
        >
          <canvas ref={chartInstanceRef} height="100"></canvas>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={() => handleViewChange("stateWise")}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "5px",
              fontWeight: activeView === "stateWise" ? "bold" : "normal",
              backgroundColor:
                activeView === "stateWise" ? "#3b82f6" : "#e5e7eb",
              color: activeView === "stateWise" ? "white" : "#374151",
            }}
          >
            State-Wise
          </button>
          <button
            onClick={() => handleViewChange("vcWise")}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "5px",
              fontWeight: activeView === "vcWise" ? "bold" : "normal",
              backgroundColor: activeView === "vcWise" ? "#3b82f6" : "#e5e7eb",
              color: activeView === "vcWise" ? "white" : "#374151",
            }}
          >
            VC-Wise
          </button>
        </div>

        <div
          className="table-container"
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            padding: "1rem",
            marginTop: "0.5rem",
          }}
        >
          <table style={{ width: "100%" }}>
            <thead>
              <tr style={{ backgroundColor: "#e5e7eb" }}>
                <th>{activeView === "stateWise" ? "State" : "VC Name"}</th>
                <th>Total Visits</th>
                <th>Completed Visits</th>
                <th>Pending Visits</th>{" "}
                {/* Replaced "Completed %" with "Pending Visits" */}
              </tr>
            </thead>
            <tbody>
              {activeView === "stateWise"
                ? Object.entries(filteredData?.stateData || {}).map(
                    ([state, data]) => (
                      <tr key={state}>
                        <td>{state}</td>
                        <td>{data.total}</td>
                        <td>{data.completed}</td>
                        <td>{data.pending}</td>{" "}
                        {/* Show pending instead of percentage */}
                      </tr>
                    )
                  )
                : Object.entries(filteredData?.vcData || {}).map(
                    ([vc, data]) => (
                      <tr key={vc}>
                        <td>{vc}</td>
                        <td>{data.total}</td>
                        <td>{data.completed}</td>
                        <td>{data.pending}</td>{" "}
                        {/* Show pending instead of percentage */}
                      </tr>
                    )
                  )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: "20px", display: "flex", gap: "15px" }}>
          <button
            style={{
              padding: "8px 12px",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              backgroundColor: "#3B82F6",
              borderRadius: "5px",
            }}
            onClick={exportAsPDF}
          >
            Export as PDF
          </button>
          <button
            style={{
              padding: "8px 12px",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              backgroundColor: "#3B82F6",
              borderRadius: "5px",
            }}
          >
            Export as CSV
          </button>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SchoolVisit;
